import { useThree, useFrame } from "@react-three/fiber";
import { useRef, useEffect, useState, isValidElement, cloneElement } from "react";
import { Vector3, Plane, Raycaster, Quaternion, MathUtils } from "three";

// Componente principale per la scena AR
export default function ARScene({
    children,
    modelUrl,
    error,
    scalingStarted,        // Stato esterno: indica se la gesture di scala è attiva
    setScalingStarted,     // Setter per scalingStarted
    setModelScale,         // Setter per la scala del modello (per comunicare all'esterno)
}) {
    // Stati locali per la gestione della visibilità e interazione del modello
    const [modelVisible, setModelVisible] = useState(false);      // Mostra/nasconde il modello
    const [hitTestSource, setHitTestSource] = useState(null);     // Sorgente per l'hit test AR
    const [modelPlaced, setModelPlaced] = useState(false);        // Indica se il modello è stato posizionato
    const [isDragging, setIsDragging] = useState(false);          // Indica se si sta trascinando il modello
    const [initialDistance, setInitialDistance] = useState(null); // Distanza iniziale tra due dita (per la scala)
    const [startTouchAngle, setStartTouchAngle] = useState(null); // Angolo iniziale tra due dita (per la rotazione)
    const [isScalingFrozen, setIsScalingFrozen] = useState(false);// Blocca temporaneamente la scala

    // Hook di React Three Fiber per accedere a scena, camera e renderer
    const { scene, camera, gl } = useThree();

    // Ref per oggetti e valori persistenti tra i render
    const directionalLightRef = useRef();         // Riferimento alla luce direzionale
    const modelRef = useRef();                    // Riferimento al gruppo del modello 3D
    const lastModelScaleRef = useRef(1);          // Scala del modello prima della gesture
    const lastModelRotationRef = useRef(0);       // Rotazione del modello prima della gesture

    const reticleRef = useRef();                  // Riferimento al reticolo di posizionamento
    const reticleScaleRef = useRef(new Vector3(0.5, 0.5, 0.5)); // Scala del reticolo
    const reticleMaterialRef = useRef();          // Materiale del reticolo (per feedback visivo)

    const shadowPlaneRef = useRef();              // Piano d'ombra sotto il modello

    const allowInteractionRef = useRef(true);     // Permette o blocca l'interazione (es. se si tocca la UI)

    // Costanti per limiti di scala e parametri vari
    const MIN_SCALE = 0.5;
    const MAX_SCALE = 6;
    const SCALE_TOLERANCE = 0.005;
    const MIN_SCALE_THRESHOLD = 0.75;   // Soglia inferiore per attivare la gesture di scala
    const MAX_SCALE_THRESHOLD = 1.35;   // Soglia superiore per attivare la gesture di scala
    const Y_SHADOW_PLANE = -0.001;      // Offset verticale per il piano d'ombra

    // Impedisce il dragging se si interagisce con elementi UI (es. bottoni)
    useEffect(() => {
        const preventDragOnUI = (event) => {
            const target = event.target;
            if (
                target.closest('button') ||
                target.closest('a')
            ) {
                allowInteractionRef.current = false;
            } else {
                allowInteractionRef.current = true;
            }
        };

        document.addEventListener('pointerdown', preventDragOnUI);

        return () => {
            document.removeEventListener('pointerdown', preventDragOnUI);
        };
    }, []);

    // Inizializza la camera e la scena per la modalità AR
    useEffect(() => {
        camera.position.set(0, 0, 0);
        camera.fov = 24;
        camera.near = 0.01;
        camera.far = 1000;
        camera.updateProjectionMatrix();

        scene.background = null;
        scene.environment = null;
    }, [scene, camera]);

    // Setup dell'hit test per il posizionamento del modello tramite AR
    useEffect(() => {
        const session = gl.xr.getSession();
        if (!session) return;

        let viewerSpace = null;

        // Richiede uno spazio di riferimento per l'hit test
        session.requestReferenceSpace("viewer").then((refSpace) => {
            viewerSpace = refSpace;
            return session.requestHitTestSource({ space: viewerSpace });
        }).then((source) => {
            setHitTestSource(source);
        });

        // Reset degli stati quando la sessione AR termina
        session.addEventListener("end", () => {
            setHitTestSource(null);
            setModelPlaced(false);
            setModelVisible(false);
            setIsDragging(false);
            setScalingStarted(false);
            setIsScalingFrozen(false);
            setStartTouchAngle(null);
            setInitialDistance(null);
            setModelScale(1);
        });

        // Gestione inizio e fine selezione (touch/click)
        const onSelectStart = () => {
            if (!allowInteractionRef.current) return;

            setIsDragging(true);
            if (reticleRef.current) reticleRef.current.visible = true;

            // Se ci sono due dita, resetta angolo e distanza iniziali e la gesture di scala
            const session = gl.xr.getSession();
            if (session && session.inputSources.length >= 2) {
                setStartTouchAngle(null);
                setInitialDistance(null);
                setScalingStarted(false);
            }
        };

        const onSelectEnd = () => {
            setIsDragging(false);
            setModelPlaced(true);
            if (reticleRef.current) reticleRef.current.visible = false;
            setStartTouchAngle(null);
            setInitialDistance(null);
            setScalingStarted(false);
        };

        session.addEventListener("selectstart", onSelectStart);
        session.addEventListener("selectend", onSelectEnd);

        return () => {
            session.removeEventListener("selectstart", onSelectStart);
            session.removeEventListener("selectend", onSelectEnd);
            // session.end().catch(() => { });
        };
    }, [gl]);

    // Funzione per posizionare inizialmente il modello e il reticolo in AR
    function initPositionInAR(pose) {
        if (pose) {
            const pos = pose.transform.position;
            const rot = pose.transform.orientation;

            if (!modelVisible) {
                setModelVisible(true);
            }

            if (!modelPlaced && modelRef.current) {
                // Posizionamento iniziale del modello
                modelRef.current.position.set(pos.x, pos.y, pos.z);
                modelRef.current.quaternion.set(rot.x, rot.y, rot.z, rot.w);
                setModelPlaced(true);
                if (reticleRef.current) reticleRef.current.visible = false;
            }

            // Aggiorna posizione e visibilità del reticolo
            if (reticleRef.current) {
                reticleRef.current.visible = !modelPlaced || isDragging;
                reticleRef.current.position.set(pos.x, pos.y, pos.z);
                reticleRef.current.rotation.set(-Math.PI / 2, 0, 0);
            }
        }
    }

    // Gestione dello spostamento del modello tramite dragging (un dito)
    function handleDraggingInAR(session, frame, referenceSpace) {
        const inputSource = session.inputSources[0];
        const targetRayPose = frame.getPose(inputSource.targetRaySpace, referenceSpace);

        if (inputSource && targetRayPose) {
            const rayOrigin = new Vector3().copy(targetRayPose.transform.position);
            const rayDirection = new Vector3(0, 0, -1).applyQuaternion(
                new Quaternion().copy(targetRayPose.transform.orientation)
            );

            // Crea un raycaster per trovare l'intersezione con il piano orizzontale
            const raycaster = new Raycaster(rayOrigin, rayDirection);

            // Interseca con il piano Y del reticolo o del modello
            const planeY = reticleRef.current?.position.y ?? modelRef.current.position.y;
            const plane = new Plane(new Vector3(0, 1, 0), -planeY);
            const intersection = new Vector3();

            if (raycaster.ray.intersectPlane(plane, intersection)) {
                // Sposta il modello e il reticolo
                modelRef.current.position.copy(intersection);
                if (reticleRef.current) {
                    updateReticleTransform(intersection);
                    reticleRef.current.scale.copy(reticleScaleRef.current);
                }
            }
        }
    }

    // Gestione di scala e rotazione tramite due dita in AR
    function handleScaleRotationInAR(session, frame, referenceSpace) {
        const [inputA, inputB] = session.inputSources;

        if (inputA && inputB && inputA.targetRaySpace && inputB.targetRaySpace) {
            const poseA = frame.getPose(inputA.targetRaySpace, referenceSpace);
            const poseB = frame.getPose(inputB.targetRaySpace, referenceSpace);

            if (poseA && poseB) {
                if (isDragging && modelRef.current && reticleRef.current) {
                    updateReticleTransform(modelRef.current.position);
                }

                // Calcola la distanza tra le due dita
                const positionA = poseA.transform.position;
                const positionB = poseB.transform.position;
                const dx = positionA.x - positionB.x;
                const dy = positionA.y - positionB.y;
                const dz = positionA.z - positionB.z;

                if (!isScalingFrozen) {
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (initialDistance === null) {
                        // Salva la distanza iniziale e la scala corrente, resetta la gesture di scala
                        setInitialDistance(distance);
                        lastModelScaleRef.current = modelRef.current.scale.x;
                        setScalingStarted(false);
                    } else {
                        let scaleFactor = distance / initialDistance;

                        // Attiva la gesture di scala solo se si esce dalla zona morta
                        if (!scalingStarted && (scaleFactor < MIN_SCALE_THRESHOLD || scaleFactor > MAX_SCALE_THRESHOLD)) {
                            setScalingStarted(true);
                        }

                        if (scalingStarted) {
                            let rawScale = lastModelScaleRef.current * scaleFactor;
                            let newScale = MathUtils.clamp(rawScale, MIN_SCALE, MAX_SCALE);

                            // Feedback visivo e limiti
                            if (rawScale <= MIN_SCALE) flashReticleColor('red', 10);
                            else if (rawScale >= MAX_SCALE) flashReticleColor('red', 10);
                            else if (Math.abs(rawScale - 1) < SCALE_TOLERANCE) {
                                flashReticleColor('limegreen', 300);
                                freezeModelTemporarily();
                            }

                            // Applica la nuova scala al modello e al reticolo
                            modelRef.current.scale.set(newScale, newScale, newScale);
                            reticleScaleRef.current.set(newScale * 0.5, newScale * 0.5, newScale * 0.5);
                            reticleRef.current.scale.copy(reticleScaleRef.current);

                            setModelScale(newScale); // Comunica la nuova scala all'esterno
                        }
                    }
                }

                // Calcola la rotazione sul piano orizzontale
                const currentAngle = Math.atan2(dx, dz);

                if (startTouchAngle === null) {
                    setStartTouchAngle(currentAngle);
                    lastModelRotationRef.current = modelRef.current.rotation.y;
                } else {
                    const deltaAngle = currentAngle - startTouchAngle;
                    modelRef.current.rotation.y = lastModelRotationRef.current + deltaAngle;
                }
            }
        }
    }

    // Aggiorna posizione e rotazione del reticolo
    function updateReticleTransform(newPosition) {
        reticleRef.current.position.copy(newPosition);
        reticleRef.current.rotation.set(-Math.PI / 2, 0, 0);
    }

    // Cambia temporaneamente il colore del reticolo per feedback visivo
    function flashReticleColor(color, duration = 300) {
        if (!reticleMaterialRef.current) return;

        const originalColor = reticleMaterialRef.current.color.clone();
        reticleMaterialRef.current.color.set(color);
        triggerHapticFeedback();

        setTimeout(() => {
            if (reticleMaterialRef.current) {
                reticleMaterialRef.current.color.set(originalColor);
            }
        }, duration);
    }

    // Vibrazione per feedback tattile (se supportato)
    function triggerHapticFeedback() {
        if (navigator.vibrate) {
            navigator.vibrate(30); // vibrazione leggera per 30ms
        }
    }

    // Congela temporaneamente la scala per evitare cambiamenti rapidi
    function freezeModelTemporarily() {
        setIsScalingFrozen(true);
        setTimeout(() => {
            setIsScalingFrozen(false);
        }, 300);
    }

    // Loop di rendering: aggiorna posizione, dragging, scala, rotazione e ombra
    useFrame((state, delta, frame) => {
        if (!hitTestSource || !frame) return;

        const referenceSpace = gl.xr.getReferenceSpace();
        const hitTestResults = frame.getHitTestResults(hitTestSource);
        const session = gl.xr.getSession();
        const inputSourcesLength = session.inputSources.length;

        if (!session) return;

        // Se c'è un risultato di hit test, posiziona modello e reticolo
        if (hitTestResults.length > 0) {
            const hit = hitTestResults[0];
            const pose = hit.getPose(referenceSpace);

            initPositionInAR(pose);
        }

        // Dragging con un dito
        if (inputSourcesLength === 1 && isDragging && modelPlaced && modelRef.current) {
            handleDraggingInAR(session, frame, referenceSpace);
        }

        // Scala e rotazione con due dita
        if (inputSourcesLength >= 2 && modelRef.current) {
            handleScaleRotationInAR(session, frame, referenceSpace);
        }

        // Aggiorna la posizione del piano d'ombra sotto il modello
        if (shadowPlaneRef.current && modelRef.current) {
            shadowPlaneRef.current.position.set(
                modelRef.current.position.x,
                modelRef.current.position.y + Y_SHADOW_PLANE,
                modelRef.current.position.z
            );
        }
    });

    // Render della scena AR: luci, reticolo, piano d'ombra e modello 3D
    return (
        <>
            {/* Luce ambientale */}
            <ambientLight intensity={1.5} />
            {/* Luce direzionale per ombre */}
            <directionalLight
                ref={directionalLightRef}
                position={[0, 5, 0]}
                intensity={3.5}
                castShadow
                shadow-mapSize={[1024, 1024]}
                shadow-camera-near={0.1}
                shadow-camera-far={10}
                shadow-camera-left={-2}
                shadow-camera-right={2}
                shadow-camera-top={2}
                shadow-camera-bottom={-2}
                shadow-bias={-0.0001}
            />

            {/* Helper per la camera della shadow */}
            {/* {directionalLightRef.current && (
                <cameraHelper args={[directionalLightRef.current.shadow.camera]} />
            )} */}
            
            {/* Reticolo per il posizionamento del modello */}
            <mesh ref={reticleRef} rotation-x={Math.PI / 2} visible={false} scale={0.5}>
                <ringGeometry args={[0.5, 0.6, 32]} />
                <meshBasicMaterial ref={reticleMaterialRef} color="#fafafa" transparent opacity={0.5} />
            </mesh>

            {/* Piano d'ombra sotto il modello */}
            <mesh ref={shadowPlaneRef} rotation-x={-Math.PI / 2} scale={0.5} receiveShadow visible={modelVisible}>
                <planeGeometry args={[2, 2]} />
                <shadowMaterial opacity={0.35} />
                {/* <meshStandardMaterial color="#333" /> */}
            </mesh>

            {/* Modello 3D */}
            <group ref={modelRef} scale={[1, 1, 1]} visible={modelVisible}>
                {!error && modelUrl && (
                    isValidElement(children) 
                        ? cloneElement(children, { isModelPlacedInAR: modelPlaced })
                        : children
                )}
            </group>
        </>
    );
}